import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Modal,
  Box,
  FormControlLabel,
  Checkbox,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Collapse,
  IconButton,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { updatedriver } from "../../api/driver";
import { updatevehicle } from "../../api/vehicle";
import axios from "axios";
import { keyUri } from "../../key";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const RouteSelectionModal = ({
  open,
  handleClose,
  routes,
  onSave,
  initialSelectedRoute,
  initialSelectedSchedules,
  initialRoutePrices,
  vehicleid,
  driver = null,
}) => {
  const [selectedRoute, setSelectedRoute] = useState([]);
  const [routePrices, setRoutePrices] = useState({});

  const [step, setStep] = useState(1);
  const dispatch = useDispatch();

  const [scheduleOptions, setScheduleOptions] = useState({});
  const [selectedSchedules, setSelectedSchedules] = useState({});
  const [expandedRoutes, setExpandedRoutes] = useState({});
  const vehicleSelectedSchedulesRef = useRef([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [dropdownOpen, setDropdownOpen] = useState({});

  // Helper: Parse "HH:mm" into total minutes
  const parseMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, mins] = timeStr.split(":").map(Number);
    return hours * 60 + mins;
  };

  // Helper: Parse "1hr 30min" duration to minutes
  const parseDurationToMinutes = (durationStr) => {
    if (!durationStr) return 0;
    let totalMinutes = 0;
    const hourMatch = durationStr.match(/(\d+)\s*hr/);
    const minMatch = durationStr.match(/(\d+)\s*min/);
    if (hourMatch) totalMinutes += parseInt(hourMatch[1], 10) * 60;
    if (minMatch) totalMinutes += parseInt(minMatch[1], 10);
    return totalMinutes;
  };

  // Get route duration in minutes
  const getRouteDuration = (routeId) => {
    const route = routes.find((r) => r._id === routeId);
    return route ? parseDurationToMinutes(route.duration) : 0;
  };
  // Build a map { routeId: reverseRouteId }
  const reverseRouteMap = useMemo(() => {
    const map = {};
    routes.forEach((route) => {
      const parts = route.name.toLowerCase().split(" to ");
      if (parts.length !== 2) return;
      const reverseName = `${parts[1].trim()} to ${parts[0].trim()}`;
      const reverseRoute = routes.find((r) => r.name.toLowerCase() === reverseName);
      if (reverseRoute) {
      map[route._id] = reverseRoute._id;
      console.log(`Reverse route found: '${route.name}' <--> '${reverseRoute.name}'`);
    } else {
      console.log(`No reverse route found for '${route.name}'`);
    }
    });
    return map;
  }, [routes]);

  // Build all selected schedule time ranges across all routes
const buildAllSelectedRanges = (
  scheduleOptions,
  selectedSchedules,
  routes,
  reverseRouteMap,
  bufferMinutes = 60
) => {
  const ranges = [];

  for (const [routeId, selectedIds] of Object.entries(selectedSchedules)) {
    const route = routes.find((r) => r._id === routeId);
    if (!route) continue;

    const duration = parseDurationToMinutes(route.duration);
    const schedulesForRoute = scheduleOptions[routeId] || [];
    const reverseRouteId = reverseRouteMap[routeId];

    selectedIds.forEach((id) => {
      const schedule = schedulesForRoute.find((s) => s._id === id);
      if (!schedule) return;
      const start = parseMinutes(schedule.start);

      // SAME ROUTE BLOCK WINDOW
      ranges.push({
        routeKey: routeId, 
        start: start - 2 * duration - 2 * bufferMinutes,
        end: start + 2 * duration + 2 * bufferMinutes,
        type: "same"
      });

      // REVERSE ROUTE BLOCK WINDOW
      if (reverseRouteId) {
        ranges.push({
          routeKey: reverseRouteId, 
          start: start - duration - bufferMinutes,
          end: start + duration + bufferMinutes,
          type: "reverse"
        });
      }
    });
  }
  return ranges;
};


  // Check if candidate schedule overlaps any existing schedule range globally
  const isOverlapping = (candidateStart, candidateDuration, buffer, allRanges) => {
    const candidateEnd = candidateStart + candidateDuration + buffer;
    return allRanges.some(
      (range) => candidateStart < range.end && candidateEnd > range.start
    );
  };

  // Determine disabled schedule ids that overlap any selected schedule across routes
const getDisabledSchedules = (
  routeId,
  scheduleOptions,
  selectedSchedules,
  routeDurationMinutes,
  bufferMinutes = 60,
  routes
) => {
  const allSelectedRanges = buildAllSelectedRanges(
    scheduleOptions,
    selectedSchedules,
    routes,
    reverseRouteMap,
    bufferMinutes
  );

  const currentSchedules = scheduleOptions[routeId] || [];

  return currentSchedules
    .filter((schedule) => {
      const start = parseMinutes(schedule.start);
      const end = start + routeDurationMinutes;

      return allSelectedRanges.some((range) => {
        // Only consider ranges affecting this route
        return (
          range.routeKey === routeId &&
          start < range.end &&
          end > range.start
        );
      });
    })
    .map((s) => s._id);
};


  useEffect(() => {
    const fetch_vehicle_route = async () => {
      try {
        const response = await axios.get(
          `${keyUri.BACKEND_URI}/api/get-vehicle/${vehicleid}`
        );
        const vehicleData = response.data;
        const routesData = vehicleData?.route_id || [];
        const routes_IdS = routesData.map((route) =>
          typeof route === "string" ? route : route._id
        );
        setSelectedRoute(routes_IdS);

        if (vehicleData.selected_schedules) {
          vehicleSelectedSchedulesRef.current = vehicleData.selected_schedules;
        }
      } catch (error) {
        console.error("Error fetching vehicle route:", error);
        setSelectedRoute([]);
      }
    };

    if (vehicleid) fetch_vehicle_route();
  }, [vehicleid, open]);

  useEffect(() => {
    if (open) {
      setSelectedRoute(initialSelectedRoute || []);
      setSelectedSchedules(initialSelectedSchedules || {});
      setRoutePrices(initialRoutePrices || {});
    }
  }, [open, initialSelectedRoute, initialSelectedSchedules, initialRoutePrices]);

  useEffect(() => {
    const fetchRouteSchedules = async () => {
      if (!selectedRoute.length) {
        setScheduleOptions({});
        return;
      }

      try {
        const responses = await Promise.all(
          selectedRoute.map((routeId) =>
            axios.get(`${keyUri.BACKEND_URI}/api/get-route/${routeId}`)
          )
        );

        const newScheduleOptions = {};
        responses.forEach((response, index) => {
          const routeId = selectedRoute[index];
          const routeSchedules = response?.data?.schedules || [];
          newScheduleOptions[routeId] = routeSchedules;
        });

        setScheduleOptions(newScheduleOptions);
      } catch (error) {
        console.error("Error fetching route schedules:", error);
      }
    };

    fetchRouteSchedules();
  }, [selectedRoute]);

  const handleCheckboxChange = (routeId) => {
    setSelectedRoute((prev) =>
      prev.includes(routeId)
        ? prev.filter((id) => id !== routeId)
        : [...prev, routeId]
    );
  };

  const handleScheduleChange = (routeId, scheduleIds) => {
    setSelectedSchedules((prev) => ({
      ...prev,
      [routeId]: scheduleIds,
    }));
  };

  const toggleRouteExpansion = (routeId) => {
    setExpandedRoutes((prev) => ({
      ...prev,
      [routeId]: !prev[routeId],
    }));
  };

  const handleSave = async () => {
    const missingPrice = selectedRoute.some(
      (routeId) =>
        !routePrices[routeId]?.price_per_Trip ||
        Number(routePrices[routeId]?.price_per_Trip) <= 0
    );

    if (missingPrice) {
      alert("Please set a price per trip for all selected routes.");
      return;
    }

    const filteredSchedules = Object.entries(selectedSchedules)
      .filter(([routeId]) => selectedRoute.includes(routeId))
      .map(([routeId, schedules]) => ({
        route: routeId,
        schedules: schedules.filter(Boolean),
        price_per_Trip: routePrices[routeId]?.price_per_Trip || 0,
      }));

    const data = {
      route_id: selectedRoute,
      selected_schedules: filteredSchedules,
      multiRoute: true,
      newVehicleId: vehicleid,
    };

    if (driver) await dispatch(updatedriver(data, driver));
    if (vehicleid) await dispatch(updatevehicle(data, vehicleid));

    if (onSave) onSave(data);

    setStep(1);
    setSelectedSchedules({});
    setSelectedRoute([]);
    setRoutePrices({});
    handleClose();
  };

  const getRouteName = (routeId) => {
    const route = routes.find((r) => r._id === routeId);
    return route ? route.name : `Route (${routeId.substring(0, 6)}...)`;
  };

  const hasSelectedSchedules = Object.values(selectedSchedules).some(
    (schedules) => schedules.length > 0
  );

  const handleReset = () => {
    setSelectedRoute([]);
    setSelectedSchedules({});
    setExpandedRoutes({});
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        className="bg-white rounded-md p-6 max-w-md w-full mx-auto mt-20"
        style={{ position: "relative", maxHeight: "90vh", overflowY: "auto" }}
      >
        {step === 1 && (
          <>
            <h2 className="text-xl mb-4">Select Route</h2>
            <input
              type="text"
              placeholder="Search routes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full mb-2 px-3 py-2 border rounded"
            />
            <div className="max-h-60 overflow-y-auto mb-4 px-2 py-2 rounded border border-gray-300 bg-white">
              {routes
                ?.filter((route) =>
                  route.name.toLowerCase().includes(searchTerm.toLowerCase())
                )
                ?.map((route) => (
                  <div key={route._id} className="flex items-center mb-2">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedRoute.includes(route._id)}
                          onChange={() => handleCheckboxChange(route._id)}
                          color="primary"
                        />
                      }
                      label={route.name}
                    />
                  </div>
                ))}
              {routes.filter((route) =>
                route.name.toLowerCase().includes(searchTerm.toLowerCase())
              ).length === 0 && (
                <p className="text-gray-500 text-sm text-center py-2">
                  No routes found
                </p>
              )}
            </div>
            <div className="flex justify-between items-center mt-4">
              <Button onClick={handleReset} variant="outlined" color="warning">
                Reset
              </Button>

              <div className="space-x-2">
                <Button
                  onClick={handleClose}
                  variant="outlined"
                  color="secondary"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => selectedRoute.length > 0 && setStep(2)}
                  variant="contained"
                  color="primary"
                  disabled={selectedRoute.length === 0}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">
              Select Time Slots
            </h2>

            <div className="flex justify-between pt-2 pb-4 border-b mb-4">
              <Button
                onClick={() => setStep(1)}
                variant="outlined"
                color="secondary"
                sx={{
                  borderRadius: "8px",
                  paddingX: 2.5,
                  paddingY: 1,
                  textTransform: "none",
                }}
              >
                Back
              </Button>
              <Button
                onClick={() => selectedRoute.length > 0 && setStep(3)}
                variant="contained"
                color="primary"
                disabled={!hasSelectedSchedules}
              >
                Next
              </Button>
            </div>

            <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
              {selectedRoute.map((routeId) => {
                const routeDuration = getRouteDuration(routeId);
                const disabledIds = getDisabledSchedules(
                  routeId,
                  scheduleOptions,
                  selectedSchedules,
                  routeDuration,
                  60,
                  routes
                );

                return (
                  <div
                    key={routeId}
                    className="border rounded-lg p-3 bg-gray-50"
                  >
                    <div
                      className="flex justify-between items-center cursor-pointer"
                      onClick={() => toggleRouteExpansion(routeId)}
                    >
                      <Typography variant="subtitle1" className="font-medium">
                        {getRouteName(routeId)}
                      </Typography>
                      <IconButton size="small">
                        {expandedRoutes[routeId] ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </div>

                    <Collapse in={expandedRoutes[routeId]}>
                      <Divider className="my-2" />
                      <FormControl fullWidth style={{ marginTop: "5px" }}>
                        <InputLabel>Time Slots</InputLabel>
                        <Select
                          multiple
                          open={dropdownOpen[routeId] || false}
                          onOpen={() =>
                            setDropdownOpen((prev) => ({
                              ...prev,
                              [routeId]: true,
                            }))
                          }
                          onClose={() =>
                            setDropdownOpen((prev) => ({
                              ...prev,
                              [routeId]: false,
                            }))
                          }
                          value={selectedSchedules[routeId] || []}
                          onChange={(e) =>
                            handleScheduleChange(
                              routeId,
                              typeof e.target.value === "string"
                                ? e.target.value.split(",")
                                : e.target.value
                            )
                          }
                          label="Time Slots"
                          variant="outlined"
                          sx={{
                            backgroundColor: "#F9FAFB",
                            borderRadius: "8px",
                          }}
                          renderValue={(selected) => {
                            const selectedScheduleNames = (
                              scheduleOptions[routeId] || []
                            )
                              .filter((schedule) =>
                                selected.includes(schedule._id)
                              )
                              .map((schedule) => schedule.start);
                            return selectedScheduleNames.join(", ");
                          }}
                          MenuProps={{
                            disablePortal: true,
                            PaperProps: {
                              style: {
                                maxHeight: 250,
                                width: "25%",
                              },
                            },
                          }}
                        >
                          {(scheduleOptions[routeId] || []).map((schedule) => {
                           const isSelected = (selectedSchedules[routeId] || []).includes(schedule._id);
const isDisabled = disabledIds.includes(schedule._id) && !isSelected;
                            return (
                              <MenuItem
                                key={schedule._id}
                                value={schedule._id}
                                disabled={isDisabled}
                              >
                                <Checkbox
                                  checked={(
                                    selectedSchedules[routeId] || []
                                  ).includes(schedule._id)}
                                  disabled={isDisabled}
                                />
                                {schedule.start}
                              </MenuItem>
                            );
                          })}
                          <MenuItem
                            disableRipple
                            sx={{
                              justifyContent: "center",
                              borderTop: "1px solid #eee",
                              mt: 1,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDropdownOpen((prev) => ({
                                ...prev,
                                [routeId]: false,
                              }));
                            }}
                          >
                            <Button size="small" variant="contained">
                              OK
                            </Button>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Collapse>
                  </div>
                );
              })}

              {selectedRoute.length === 0 && (
                <Typography className="text-center py-4 text-gray-500">
                  No routes selected. Go back to select routes.
                </Typography>
              )}
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">
              Set Route Prices
            </h2>

            <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
              {selectedRoute.map((routeId) => (
                <div key={routeId} className="border rounded-lg p-3 bg-gray-50">
                  <Typography variant="subtitle1" className="font-medium mb-2">
                    {getRouteName(routeId)}
                  </Typography>

                  <div className="flex gap-3">
                    <FormControl fullWidth>
                      <input
                        type="number"
                        value={routePrices[routeId]?.price_per_Trip || ""}
                        onChange={(e) =>
                          setRoutePrices((prev) => ({
                            ...prev,
                            [routeId]: {
                              ...prev[routeId],
                              price_per_Trip: e.target.value,
                            },
                          }))
                        }
                        className="border px-2 py-1 rounded"
                        placeholder="Enter Price per Trip for Shared Cabs "
                      />
                    </FormControl>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-between pt-2 pb-4 border-t mt-4">
              <Button
                onClick={() => setStep(2)}
                variant="outlined"
                color="secondary"
                sx={{ borderRadius: "8px", paddingX: 2.5, paddingY: 1 }}
              >
                Back
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                color="primary"
                sx={{ borderRadius: "8px", paddingX: 3, paddingY: 1 }}
              >
                Save
              </Button>
            </div>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default RouteSelectionModal;
